import type { TodoNoteCategoryDraft, TodoNoteItemDraft } from '../types/todoNote';

export interface TodoNoteWorkspaceSnapshot {
  categories: TodoNoteCategoryDraft[];
  items: TodoNoteItemDraft[];
}

/** v3: 대시보드 Todo 소스를 기본 공유 보드로 통일 — 레거시 LS 스냅샷 무효화 */
const TODO_NOTE_CACHE_VERSION = 'v3';

type TodoNoteWorkspaceCachePayload = TodoNoteWorkspaceSnapshot & {
  savedAt: number;
};

function cacheScopeKey(noteWorkspaceId: string | null): string {
  return noteWorkspaceId ?? 'account';
}

function storageKey(userId: string, noteWorkspaceId: string | null): string {
  return `cp.todoNoteWorkspace.${TODO_NOTE_CACHE_VERSION}.${userId}.${cacheScopeKey(noteWorkspaceId)}`;
}

export function isTodoNoteWorkspaceCacheStorageKey(
  key: string | null | undefined,
  userId: string,
  noteWorkspaceId: string | null
): boolean {
  if (!key || !userId) return false;
  return key === storageKey(userId, noteWorkspaceId);
}

function normalizeSnapshot(
  snapshot: TodoNoteWorkspaceSnapshot,
  noteWorkspaceId: string | null
): TodoNoteWorkspaceSnapshot {
  return {
    categories: snapshot.categories.map((c) => ({
      ...c,
      note_memo: typeof c.note_memo === 'string' ? c.note_memo : '',
      workspace_id: c.workspace_id ?? noteWorkspaceId,
    })),
    items: snapshot.items.map((it) => ({
      ...it,
      workspace_id: it.workspace_id ?? noteWorkspaceId,
    })),
  };
}

export function loadTodoNoteWorkspaceCache(
  userId: string,
  noteWorkspaceId: string | null
): { found: boolean; data: TodoNoteWorkspaceSnapshot; savedAt?: number } {
  const empty: TodoNoteWorkspaceSnapshot = { categories: [], items: [] };
  if (!userId || typeof window === 'undefined') return { found: false, data: empty };
  try {
    const raw = window.localStorage.getItem(storageKey(userId, noteWorkspaceId));
    if (!raw) return { found: false, data: empty };
    const parsed = JSON.parse(raw) as Partial<TodoNoteWorkspaceCachePayload> | null;
    if (!parsed || typeof parsed !== 'object') return { found: false, data: empty };
    const data = normalizeSnapshot(
      {
        categories: Array.isArray(parsed.categories) ? (parsed.categories as TodoNoteCategoryDraft[]) : [],
        items: Array.isArray(parsed.items) ? (parsed.items as TodoNoteItemDraft[]) : [],
      },
      noteWorkspaceId
    );
    const savedAt = typeof parsed.savedAt === 'number' && parsed.savedAt > 0 ? parsed.savedAt : undefined;
    return { found: true, data, savedAt };
  } catch {
    return { found: false, data: empty };
  }
}

export function saveTodoNoteWorkspaceCache(
  userId: string,
  noteWorkspaceId: string | null,
  snapshot: TodoNoteWorkspaceSnapshot
): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    const payload: TodoNoteWorkspaceCachePayload = {
      ...normalizeSnapshot(snapshot, noteWorkspaceId),
      savedAt: Date.now(),
    };
    window.localStorage.setItem(storageKey(userId, noteWorkspaceId), JSON.stringify(payload));
  } catch {
    // ignore quota/private mode errors
  }
}

export function invalidateTodoNoteWorkspaceCache(userId: string, noteWorkspaceId: string | null): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey(userId, noteWorkspaceId));
  } catch {
    // ignore
  }
}
